import React, {useState, useEffect} from 'react';
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { setDisplayTermsPopup, setHasAcceptedTerms } from '../../../state/slices/user';
import apiClient from '../../../auth/apiClient';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@material-ui/core';

import { useStyles } from './styles';
import UserService from '../../../services/UserService';
import useCurrentFacility from '../../../hooks/useCurrentFacility';
import { useAppActionContext } from '../../../providers/AppStore/AppActionProvider';

import TermsMarkdownContent from './TermsMarkdownContent';


export const TermsDialog = ({
  termsTitle = 'Terms & Conditions',
  termsContent = null,
  checkboxLabel = 'I agree to the terms and conditions',
  acceptLabel = 'Accept and Continue',
  cancelLabel = 'Close',
  disableBackdropClose = true,
  disableEscClose = false,
}) => {
  const userService = new UserService(apiClient);
  const classes = useStyles();
  const dispatch = useDispatch();
  const isGetReportRoute = window.location.pathname.includes("getreport")
  const [accepted, setAccepted] = useState(false);
  const [daysLeft, setDaysLeft] = useState(30);
  const [daysLabel, setDaysLabel] = useState('');
  const { logOut } = useAppActionContext();
  const currentUser = useSelector((state) => state.user, shallowEqual);
  const { facilityID } = useCurrentFacility();
  const scopeAwareFacilityID = useSelector((state) => state.entityScope?.facilityGroupId || facilityID);
  const presentedAt = currentUser?.termsDetails?.presentedAt;

  /*
  // this code will be usable once we implement AO-28810
  const fetchTermsContent = async () => {
    try {
      const response = await userService.getTermsContent(currentUser?.UserID, scopeAwareFacilityID)
      console.log(response);
    }catch(e) {
      console.log(e);
    }
  }

  
  useEffect(() => {
    fetchTermsContent();
  }, [])
  */

  const calculateDaysLeft = (date) => {
    if (!date) return 0;
    const creation = new Date(date);
    if (isNaN(creation)) return 0;
    const expiry = new Date(creation.getTime() + 30 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const diffMs = expiry - now;
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  };

  useEffect(() => {
    if (presentedAt) setDaysLeft(calculateDaysLeft(presentedAt))
  }, [presentedAt])

  useEffect(() => {
    const cappedDays = Math.max(0, Math.min(daysLeft, 30));
    setDaysLabel(
      cappedDays === 1 ? "1 day" : `${cappedDays} days`
    );
  }, [daysLeft])

  const handleClose = (event, reason) => {
    if (disableBackdropClose && reason === 'backdropClick') return;
    if (disableEscClose && reason === 'escapeKeyDown') return;
    dispatch(setDisplayTermsPopup(false));
    setAccepted(false);
    if(!currentUser?.hasAcceptedTerms && daysLeft <=0) {
      logOut()
    }
  };

  const handleAccept = async () => {
    try {
      const response = await userService.acceptLatestTerms(currentUser?.UserID, scopeAwareFacilityID, currentUser?.termsDetails?.version);
      dispatch(setDisplayTermsPopup(false));
      dispatch(setHasAcceptedTerms(true));
      setAccepted(false);
    } catch(e) {
      console.error(e);
    }
  }

  return (
    <>
      {currentUser?.termsDetails?.version && !isGetReportRoute &&
        <Dialog
          open={currentUser?.displayTermsPopup}
          onClose={handleClose}
          aria-labelledby="terms-dialog-title"
          fullWidth
          maxWidth="sm"
          classes={{ paper: classes.paper }}
        >
          <DialogTitle id="terms-dialog-title" className={classes.title}>
            {termsTitle}
          </DialogTitle>

          <DialogContent dividers className={classes.content}>
            <div className={classes.outer}>
              <div
                className={classes.scrollBox}
                role="document"
                aria-label="Terms content"
                tabIndex={0}
              >
                {termsContent ? (
                  <Typography variant="body2" component="div" style={{ whiteSpace: 'pre-wrap' }}>
                    {termsContent}
                  </Typography>
                ) : (
                  <TermsMarkdownContent />
                )}
              </div>

              {!currentUser?.hasAcceptedTerms ?
                <>
                  <div className={classes.controlsRow}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={accepted}
                          onChange={(e) => setAccepted(e.target.checked)}
                          inputProps={{ 'aria-label': checkboxLabel }}
                        />
                      }
                      label={checkboxLabel}
                    />

                    <div className={classes.buttonsRow}>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={!accepted}
                        onClick={handleAccept}
                      >
                        {acceptLabel}
                      </Button>
                      <Button variant="outlined" onClick={handleClose}>
                        {cancelLabel}
                      </Button>
                    </div>
                  </div>

                  <Typography variant="body2" className={classes.footer}>
                    {daysLeft >= 1
                      ? `You have ${daysLabel} left to use the platform.`
                      : 'Your access has expired. To continue using the platform, please review and accept the terms and conditions.'}
                  </Typography>

                </> :
                              <div className={classes.controlsRowFooter}>
                  <div className={classes.buttonsRow}>
                    <Button variant="outlined" onClick={handleClose}>
                      {cancelLabel}
                    </Button>
                  </div>
                </div>
              }
            </div>
          </DialogContent>
        </Dialog>
      }
    </>
  );
}

export default TermsDialog